import React, { useState, useCallback, useMemo } from 'react';
import AsyncSelect from 'react-select/async';

const QuoteItemBootstrap = ({
  item,
  onUpdate,
  onRemove,
  availableArticles = [],
  index,
  isLast,
  defaultVatRate = 25,
  onSelectArticle,
  currency = 'SEK'
}) => {

  const [expanded, setExpanded] = useState(!item.description || item.isNew);

  // Handle field changes
  const handleChange = useCallback((field, value) => {
    onUpdate({
      ...item,
      [field]: value
    });
  }, [item, onUpdate]);

  // Format currency
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: currency
    }).format(value || 0);
  }, [currency]);

  // Format article display
  const getArticleLabel = useCallback((article) => {
    return `${article.productcode || ''} - ${article.description || ''} (${formatCurrency(article.unitprice || 0)})`;
  }, [formatCurrency]);

  // Filter articles by search term
  const filterArticles = useCallback((inputValue) => {
    const searchTerm = inputValue.toLowerCase();
    return availableArticles.filter(article =>
      article.productcode?.toLowerCase().includes(searchTerm) ||
      article.description?.toLowerCase().includes(searchTerm)
    );
  }, [availableArticles]);

  // Load article options for the async select
  const loadArticleOptions = useCallback((inputValue) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const filteredArticles = filterArticles(inputValue);
        const options = filteredArticles.slice(0, 50).map(article => ({
          value: article.id,
          label: getArticleLabel(article),
          article: article
        }));
        resolve(options);
      }, 300);
    });
  }, [filterArticles, getArticleLabel]);

  // Handle article selection from AsyncSelect
  const handleAsyncSelectArticle = useCallback((selectedOption) => {

    if (selectedOption && onSelectArticle) {
      onSelectArticle(item.id, selectedOption.value);
    }
  }, [item.id, onSelectArticle]);

  // Calculate totals
  const subtotal = useMemo(() => {
    const price = parseFloat(item.unitprice) || 0;
    const qty = parseFloat(item.quantity) || 0;
    return price * qty;
  }, [item.unitprice, item.quantity]);
  const taxRate = useMemo(() => parseFloat(item.tax_rate?.replace('%', '') || 0), [item.tax_rate]);
  const taxAmount = useMemo(() => subtotal * (taxRate / 100), [subtotal, taxRate]);
  const total = useMemo(() => subtotal + taxAmount, [subtotal, taxAmount]);

  // Get unit display name
  const getUnitDisplayName = useCallback((unit) => {
    const unitMap = {
      'Hour': 'tim',
      'Piece': 'st',
      'Day': 'dag',
      'Month': 'mån',
      'Package': 'frp',
      'Set': 'set',
      'Kilogram': 'kg',
      'Liter': 'l',
      'Meter': 'm',
      'Square Meter': 'm²',
      'Cubic Meter': 'm³',
      // Legacy mappings for backward compatibility
      'Kg': 'kg',
      'st': 'st',
      'tim': 'tim',
      'dag': 'dag',
      'mån': 'mån',
      'frp': 'frp',
      'kg': 'kg',
      'm': 'm'
    };
    return unitMap[unit] || unit;
  }, []);

  // Get item display title - prioritize description over productcode when productcode is empty
  const getItemDisplayTitle = useCallback(() => {
    if (item.productcode && item.productcode.trim() !== '') {
      return item.productcode;
    } else if (item.description && item.description.trim() !== '') {
      // Show first 30 characters of description for title
      return item.description.length > 30
        ? item.description.substring(0, 30) + '...'
        : item.description;
    } else {
      return 'Ny artikel';
    }
  }, [item.productcode, item.description]);

  // Get item display subtitle when expanded is false
  const getItemDisplaySubtitle = useCallback(() => {
    if (item.productcode && item.productcode.trim() !== '' && item.description) {
      // If we have productcode, show description as subtitle
      return item.description.length > 50
        ? item.description.substring(0, 50) + '...'
        : item.description;
    } else if (!item.productcode || item.productcode.trim() === '') {
      // If no productcode but we showed description as title, show type and unit as subtitle
      return `${item.type || 'Goods'} - ${getUnitDisplayName(item.unit || 'st')}`;
    }
    return null;
  }, [item.productcode, item.description, item.type, item.unit, getUnitDisplayName]);

  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  return (
    <div className="card mb-3 border border-primary border-dashed">
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center flex-grow-1">
            <button
              type="button"
              className="btn btn-sm me-2"
              onClick={toggleExpanded}
              aria-label={expanded ? 'Minimera' : 'Expandera'}
            >
              <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}></i>
            </button>
            <div className="flex-grow-1">
              <div className="d-flex align-items-center">
                <strong>{getItemDisplayTitle()}</strong>
                {item.type && (
                  <span className="badge bg-secondary ms-2 small">
                    {item.type === 'Service' ? 'Tjänst' :
                      item.type === 'Goods' ? 'Varor' :
                        item.type === 'Product' ? 'Produkt' : item.type}
                  </span>
                )}
              </div>
              {!expanded && getItemDisplaySubtitle() && (
                <div className="text-muted small mt-1">
                  {getItemDisplaySubtitle()}
                </div>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center">
            <span className="me-3 text-end">
              <div className="fw-medium">{formatCurrency(total)}</div>
              <small className="text-muted">
                {item.quantity || 1} {getUnitDisplayName(item.unit || 'st')} × {formatCurrency(item.unitprice || 0)}
              </small>
            </span>
            <i
              className="bi bi-trash text-danger"
              style={{ cursor: 'pointer', fontSize: '1.2rem' }}
              onClick={() => onRemove('remove')}
              role="button"
              aria-label="Ta bort artikel"
            ></i>
          </div>
        </div>

        {expanded && (
          <div className="mt-3 animate__animated animate__fadeIn">
            {availableArticles.length > 0 && (
              <div className="mb-3">
                <label htmlFor={`article-select-${item.id}`} className="form-label">Välj artikel</label>
                <AsyncSelect
                  id={`article-select-${item.id}`}
                  cacheOptions
                  defaultOptions={availableArticles.slice(0, 10).map(article => ({
                    value: article.id,
                    label: getArticleLabel(article)
                  }))}
                  value={item.articleId ? {
                    value: item.articleId,
                    label: getArticleLabel(
                      availableArticles.find(a => a.id === item.articleId) || {}
                    )
                  } : null}
                  loadOptions={loadArticleOptions}
                  onChange={handleAsyncSelectArticle}
                  isClearable
                  placeholder="Sök efter artikelkod eller beskrivning..."
                  noOptionsMessage={() => "Inga artiklar hittades"}
                  loadingMessage={() => "Söker artiklar..."}
                  classNamePrefix="react-select"
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#80bdff' : '#ced4da',
                      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(8, 154, 25, 0.25)' : null,
                      '&:hover': {
                        borderColor: state.isFocused ? '#80bdff' : '#adb5bd'
                      }
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 1000
                    })
                  }}
                />
              </div>
            )}

            <div className="row mb-3">
              <div className="col-md-4">
                <label htmlFor={`productcode-${item.id}`} className="form-label">Artikelkod</label>
                <input
                  type="text"
                  className="form-control"
                  id={`productcode-${item.id}`}
                  value={item.productcode || ''}
                  onChange={(e) => handleChange('productcode', e.target.value)}
                  placeholder="T.ex. ART-001"
                />
              </div>

              <div className="col-md-8">
                <label htmlFor={`description-${item.id}`} className="form-label">Beskrivning</label>
                <input
                  type="text"
                  className="form-control"
                  id={`description-${item.id}`}
                  value={item.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Beskrivning av artikeln"
                />
              </div>
            </div>

            <div className="row gx-2 mb-3">
              <div className="col">
                <label htmlFor={`quantity-${item.id}`} className="form-label">Antal</label>
                <input
                  type="number"
                  className="form-control"
                  id={`quantity-${item.id}`}
                  min="1"
                  step="0.5"
                  placeholder="t.ex. 2.5"
                  value={item.quantity || 1}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string for better UX while typing
                    if (value === '') {
                      handleChange('quantity', '');
                      return;
                    }
                    const parsed = parseFloat(value);
                    if (!isNaN(parsed) && parsed > 0) {
                      handleChange('quantity', parsed);
                    }
                  }}
                  onBlur={(e) => {
                    // On blur, ensure we have a valid number
                    const value = parseFloat(e.target.value);
                    if (isNaN(value) || value < 0.001) {
                      handleChange('quantity', 1);
                    }
                  }}
                />
              </div>

              <div className="col">
                <label htmlFor={`unit-${item.id}`} className="form-label">Enhet</label>
                <select
                  className="form-select"
                  id={`unit-${item.id}`}
                  value={item.unit || 'Piece'}
                  onChange={(e) => handleChange('unit', e.target.value)}
                >
                  <option value="Piece">st (Piece)</option>
                  <option value="Hour">tim (Hour)</option>
                  <option value="Day">dag (Day)</option>
                  <option value="Month">mån (Month)</option>
                  <option value="Package">frp (Package)</option>
                  <option value="Set">set (Set)</option>
                  <option value="Kilogram">kg (Kilogram)</option>
                  <option value="Liter">l (Liter)</option>
                  <option value="Meter">m (Meter)</option>
                  <option value="Square Meter">m² (Square Meter)</option>
                  <option value="Cubic Meter">m³ (Cubic Meter)</option>
                </select>
              </div>

              <div className="col">
                <label htmlFor={`type-${item.id}`} className="form-label">Artikeltyp</label>
                <select
                  className="form-select"
                  id={`type-${item.id}`}
                  value={item.type || 'Goods'}
                  onChange={(e) => handleChange('type', e.target.value)}
                >
                  <option value="Goods">Varor</option>
                  <option value="Service">Tjänst</option>
                </select>
              </div>

              <div className="col">
                <label htmlFor={`unitprice-${item.id}`} className="form-label">Pris (kr)</label>
                <input
                  type="number"
                  className="form-control"
                  id={`unitprice-${item.id}`}
                  min="0"
                  step="0.01"
                  value={item.unitprice || 0}
                  onChange={(e) => handleChange('unitprice', parseFloat(e.target.value) || 0)}
                />
              </div>

              <div className="col">
                <label htmlFor={`tax_rate-${item.id}`} className="form-label">Moms (%)</label>
                <select
                  className="form-select"
                  id={`tax_rate-${item.id}`}
                  value={item.tax_rate?.replace('%', '') || defaultVatRate}
                  onChange={(e) => handleChange('tax_rate', `${e.target.value}%`)}
                >
                  <option value="0">0</option>
                  <option value="6">6</option>
                  <option value="12">12</option>
                  <option value="25">25</option>
                </select>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center pt-2 border-top">
              <span className="text-muted">
                Delsumma: {formatCurrency(subtotal)}
              </span>
              <span className="fw-medium">
                Moms ({item.tax_rate || '0%'}): {formatCurrency(taxAmount)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteItemBootstrap;





// import React, { useState, useCallback, useMemo } from 'react';
// import AsyncSelect from 'react-select/async';

// const QuoteItemBootstrap = ({
//   item,
//   onUpdate,
//   onRemove,
//   availableItems = [],
//   index,
//   isLast,
//   defaultVatRate = 25,
//   onSelectArticle,
//   currency = 'SEK'
// }) => {
//   const [expanded, setExpanded] = useState(!item.description || item.isNew);

//   // Handle field changes
//   const handleChange = useCallback((field, value) => {
//     onUpdate({
//       ...item,
//       [field]: value
//     });
//   }, [item, onUpdate]);

//   // Format article display
//   const getArticleLabel = useCallback((article) => {
//     return `${article.productcode || ''} - ${article.description || ''} (${formatCurrency(article.unitprice || 0)})`;
//   }, []);

//   // Filter articles by search term
//   const filterArticles = useCallback((inputValue) => {
//     const searchTerm = inputValue.toLowerCase();
//     return availableItems.filter(article =>
//       article.productcode?.toLowerCase().includes(searchTerm) ||
//       article.description?.toLowerCase().includes(searchTerm)
//     );
//   }, [availableItems]);

//   // Load article options for the async select
//   const loadArticleOptions = useCallback((inputValue) => {
//     return new Promise((resolve) => {
//       setTimeout(() => {
//         const filteredArticles = filterArticles(inputValue);
//         const options = filteredArticles.slice(0, 50).map(article => ({
//           value: article.id,
//           label: getArticleLabel(article),
//           article: article
//         }));
//         resolve(options);
//       }, 300);
//     });
//   }, [filterArticles, getArticleLabel]);

//   // Handle article selection from AsyncSelect
//   const handleAsyncSelectArticle = useCallback((selectedOption) => {
//     if (selectedOption && onSelectArticle) {
//       onSelectArticle(item.id, selectedOption.value);
//     }
//   }, [item.id, onSelectArticle]);

//   // Calculate totals
//   const subtotal = useMemo(() => item.unitprice * item.quantity, [item.unitprice, item.quantity]);
//   const taxRate = useMemo(() => parseFloat(item.tax_rate?.replace('%', '') || 0), [item.tax_rate]);
//   const taxAmount = useMemo(() => subtotal * (taxRate / 100), [subtotal, taxRate]);
//   const total = useMemo(() => subtotal + taxAmount, [subtotal, taxAmount]);

//   // Format currency
//   const formatCurrency = useCallback((value) => {
//     return new Intl.NumberFormat('sv-SE', {
//       style: 'currency',
//       currency: currency
//     }).format(value);
//   }, [currency]);

//   const toggleExpanded = useCallback(() => {
//     setExpanded(!expanded);
//   }, [expanded]);

//   return (
//     <div className="card mb-3 border border-primary border-dashed">
//       <div className="card-body p-3">
//         <div className="d-flex justify-content-between align-items-center">
//           <div className="d-flex align-items-center">
//             <button
//               type="button"
//               className="btn btn-sm me-2"
//               onClick={toggleExpanded}
//               aria-label={expanded ? 'Minimera' : 'Expandera'}
//             >
//               <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}></i>
//             </button>
//             <div>
//               <strong>{item.productcode || 'Ny artikel'}</strong>
//               {!expanded && item.description && (
//                 <span className="text-muted ms-2 small">{item.description}</span>
//               )}
//             </div>
//           </div>
//           <div className="d-flex align-items-center">
//             <span className="me-3 text-end">
//               <div>{formatCurrency(total)}</div>
//               <small className="text-muted">
//                 {item.quantity} {item.unit === 'Hour' ? 'tim' : item.unit} ×
//                 {formatCurrency(item.unitprice)}
//               </small>
//             </span>
//             <i
//               className="bi bi-trash text-danger"
//               style={{ cursor: 'pointer', fontSize: '1.2rem' }}
//               onClick={() => onRemove('remove')}
//               role="button"
//               aria-label="Ta bort artikel"
//             ></i>
//           </div>
//         </div>

//         {expanded && (
//           <div className="mt-3 animate__animated animate__fadeIn">
//             {availableItems.length > 0 && (
//               <div className="mb-3">
//                 <label htmlFor={`article-select-${item.id}`} className="form-label">Välj artikel</label>
//                 <AsyncSelect
//                   id={`article-select-${item.id}`}
//                   cacheOptions
//                   defaultOptions={availableItems.slice(0, 10).map(article => ({
//                     value: article.id,
//                     label: getArticleLabel(article)
//                   }))}
//                   value={item.articleId ? {
//                     value: item.articleId,
//                     label: getArticleLabel(
//                       availableItems.find(a => a.id === item.articleId) || {}
//                     )
//                   } : null}
//                   loadOptions={loadArticleOptions}
//                   onChange={handleAsyncSelectArticle}
//                   isClearable
//                   placeholder="Sök efter artikelkod eller beskrivning..."
//                   noOptionsMessage={() => "Inga artiklar hittades"}
//                   loadingMessage={() => "Söker artiklar..."}
//                   classNamePrefix="react-select"
//                   styles={{
//                     control: (baseStyles, state) => ({
//                       ...baseStyles,
//                       borderColor: state.isFocused ? '#80bdff' : '#ced4da',
//                       boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(8, 154, 25, 0.25)' : null,
//                       '&:hover': {
//                         borderColor: state.isFocused ? '#80bdff' : '#adb5bd'
//                       }
//                     }),
//                     menu: (baseStyles) => ({
//                       ...baseStyles,
//                       zIndex: 1000
//                     })
//                   }}
//                 />
//               </div>
//             )}

//             <div className="row mb-3">
//               <div className="col-md-4">
//                 <label htmlFor={`productcode-${item.id}`} className="form-label">Artikelkod</label>
//                 <input
//                   type="text"
//                   className="form-control"
//                   id={`productcode-${item.id}`}
//                   value={item.productcode || ''}
//                   onChange={(e) => handleChange('productcode', e.target.value)}
//                   placeholder="T.ex. ART-001"
//                 />
//               </div>

//               <div className="col-md-8">
//                 <label htmlFor={`description-${item.id}`} className="form-label">Beskrivning</label>
//                 <input
//                   type="text"
//                   className="form-control"
//                   id={`description-${item.id}`}
//                   value={item.description || ''}
//                   onChange={(e) => handleChange('description', e.target.value)}
//                   placeholder="Beskrivning av artikeln"
//                 />
//               </div>
//             </div>

//             <div className="row gx-2 mb-3">
//               <div className="col">
//                 <label htmlFor={`quantity-${item.id}`} className="form-label">Antal</label>
//                 <input
//                   type="number"
//                   className="form-control"
//                   id={`quantity-${item.id}`}
//                   min="1"
//                   value={item.quantity || 1}
//                   onChange={(e) => handleChange('quantity', Math.max(1, parseInt(e.target.value) || 0))}
//                 />
//               </div>

//               <div className="col">
//                 <label htmlFor={`unit-${item.id}`} className="form-label">Enhet</label>
//                 <select
//                   className="form-select"
//                   id={`unit-${item.id}`}
//                   value={item.unit || 'st'}
//                   onChange={(e) => handleChange('unit', e.target.value)}
//                 >
//                   <option value="st">st</option>
//                   <option value="tim">tim</option>
//                   <option value="dag">dag</option>
//                   <option value="mån">mån</option>
//                   <option value="frp">frp</option>
//                   <option value="kg">kg</option>
//                   <option value="m">m</option>
//                 </select>
//               </div>

//               <div className="col">
//                 <label htmlFor={`type-${item.id}`} className="form-label">Artikeltyp</label>
//                 <select
//                   className="form-select"
//                   id={`type-${item.id}`}
//                   value={item.type || 'Product'}
//                   onChange={(e) => handleChange('type', e.target.value)}
//                 >
//                   <option value="Product">Produkt</option>
//                   <option value="Service">Tjänst</option>
//                   <option value="Text">Textrad</option>
//                 </select>
//               </div>

//               <div className="col">
//                 <label htmlFor={`unitprice-${item.id}`} className="form-label">Pris (kr)</label>
//                 <input
//                   type="number"
//                   className="form-control"
//                   id={`unitprice-${item.id}`}
//                   min="0"
//                   step="0.01"
//                   value={item.unitprice || 0}
//                   onChange={(e) => handleChange('unitprice', parseFloat(e.target.value) || 0)}
//                 />
//               </div>

//               <div className="col">
//                 <label htmlFor={`tax_rate-${item.id}`} className="form-label">Moms (%)</label>
//                 <select
//                   className="form-select"
//                   id={`tax_rate-${item.id}`}
//                   value={item.tax_rate?.replace('%', '') || defaultVatRate}
//                   onChange={(e) => handleChange('tax_rate', `${e.target.value}%`)}
//                 >
//                   <option value="0">0%</option>
//                   <option value="6">6%</option>
//                   <option value="12">12%</option>
//                   <option value="25">25%</option>
//                 </select>
//               </div>
//             </div>

//             <div className="d-flex justify-content-between align-items-center pt-2 border-top">
//               <span className="text-muted">
//                 Delsumma: {formatCurrency(subtotal)}
//               </span>
//               <span className="fw-medium">
//                 Moms ({item.tax_rate || '0%'}): {formatCurrency(taxAmount)}
//               </span>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default QuoteItemBootstrap;



// // import React, { useState } from 'react';
// // import AsyncSelect from 'react-select/async';

// // const QuoteItemBootstrap = ({ 
// //   item, 
// //   onUpdate, 
// //   onRemove,
// //   availableItems = [],
// //   index,
// //   isLast,
// //   defaultVatRate = 25,
// //   onSelectArticle
// // }) => {
// //   const [expanded, setExpanded] = useState(!item.description);

// //   const handleChange = (field, value) => {
// //     onUpdate({
// //       ...item,
// //       [field]: value
// //     });
// //   };

// //   // Format article display
// //   const getArticleLabel = (article) => {
// //     return `${article.productcode || ''} - ${article.description || ''} (${formatCurrency(article.unitprice || 0)})`;
// //   };

// //   // Filter articles by search term
// //   const filterArticles = (inputValue) => {
// //     return availableItems.filter(article =>
// //       article.productcode?.toLowerCase().includes(inputValue.toLowerCase()) ||
// //       article.description?.toLowerCase().includes(inputValue.toLowerCase())
// //     );
// //   };

// //   // Load article options for the async select
// //   const loadArticleOptions = (inputValue) => {
// //     return new Promise((resolve) => {
// //       setTimeout(() => {
// //         const filteredArticles = filterArticles(inputValue);
// //         const options = filteredArticles.map(article => ({
// //           value: article.id,
// //           label: getArticleLabel(article),
// //           article: article
// //         }));
// //         resolve(options);
// //       }, 300);
// //     });
// //   };

// //   // Handle article selection from AsyncSelect
// //   const handleAsyncSelectArticle = (selectedOption) => {
// //     if (selectedOption) {
// //       if (onSelectArticle) {
// //         onSelectArticle(item.id, selectedOption.value);
// //       }
// //     }
// //   };

// //   // Calculate totals
// //   const subtotal = item.unitprice * item.quantity;
// //   const taxRate = parseFloat(item.tax_rate?.replace('%', '') || 0);
// //   const taxAmount = subtotal * (taxRate / 100);
// //   const total = subtotal + taxAmount;
  
// //   // Format currency
// //   const formatCurrency = (value) => {
// //     return new Intl.NumberFormat('sv-SE', { 
// //       style: 'currency', 
// //       currency: 'SEK' 
// //     }).format(value);
// //   };

// //   const toggleExpanded = () => {
// //     setExpanded(!expanded);
// //   };

// //   return (
// //     <div className="card mb-3 border border-primary border-dashed">
// //       <div className="card-body p-3">
// //         <div className="d-flex justify-content-between align-items-center">
// //           <div className="d-flex align-items-center">
// //             <button
// //               type="button"
// //               className="btn btn-sm me-2"
// //               onClick={toggleExpanded}
// //               aria-label={expanded ? 'Minimera' : 'Expandera'}
// //             >
// //               <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}></i>
// //             </button>
// //             <div>
// //               <strong>{item.productcode || 'Ny artikel'}</strong>
// //               {!expanded && item.description && (
// //                 <span className="text-muted ms-2 small">{item.description}</span>
// //               )}
// //             </div>
// //           </div>
// //           <div className="d-flex align-items-center">
// //             <span className="me-3 text-end">
// //               <div>{formatCurrency(total)}</div>
// //               <small className="text-muted">
// //                 {item.quantity} {item.unit === 'Hour' ? 'tim' : item.unit} ×
// //                 {formatCurrency(item.unitprice)}
// //               </small>
// //             </span>
// //             <i
// //               className="bi bi-trash text-danger"
// //               style={{ cursor: 'pointer', fontSize: '1.2rem' }}
// //               onClick={() => onRemove('remove')}
// //               role="button"
// //               aria-label="Ta bort artikel"
// //             ></i>
// //           </div>
// //         </div>

// //         {expanded && (
// //           <div className="mt-3 animate__animated animate__fadeIn">
// //             <div className="mb-3">
// //               <label htmlFor={`article-select-${item.id}`} className="form-label">Välj artikel</label>
// //               <AsyncSelect
// //                 id={`article-select-${item.id}`}
// //                 cacheOptions
// //                 defaultOptions={availableItems.map(article => ({
// //                   value: article.id,
// //                   label: getArticleLabel(article)
// //                 }))}
// //                 value={item.articleId ? {
// //                   value: item.articleId,
// //                   label: getArticleLabel(
// //                     availableItems.find(a => a.id === item.articleId) || {}
// //                   )
// //                 } : null}
// //                 loadOptions={loadArticleOptions}
// //                 onChange={handleAsyncSelectArticle}
// //                 isClearable
// //                 placeholder="Sök efter artikelkod eller beskrivning..."
// //                 noOptionsMessage={() => "Inga artiklar hittades"}
// //                 loadingMessage={() => "Söker artiklar..."}
// //                 classNamePrefix="react-select"
// //                 styles={{
// //                   control: (baseStyles, state) => ({
// //                     ...baseStyles,
// //                     borderColor: state.isFocused ? '#80bdff' : '#ced4da',
// //                     boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(8, 154, 25, 0.25)' : null,
// //                     '&:hover': {
// //                       borderColor: state.isFocused ? '#80bdff' : '#adb5bd'
// //                     }
// //                   }),
// //                   menu: (baseStyles) => ({
// //                     ...baseStyles,
// //                     zIndex: 1000
// //                   })
// //                 }}
// //               />
// //             </div>

// //             <div className="row mb-3">
// //               <div className="col-md-4">
// //                 <label htmlFor={`productcode-${item.id}`} className="form-label">Artikelkod</label>
// //                 <input
// //                   type="text"
// //                   className="form-control"
// //                   id={`productcode-${item.id}`}
// //                   value={item.productcode || ''}
// //                   onChange={(e) => handleChange('productcode', e.target.value)}
// //                   placeholder="T.ex. ART-001"
// //                 />
// //               </div>

// //               <div className="col-md-8">
// //                 <label htmlFor={`description-${item.id}`} className="form-label">Beskrivning</label>
// //                 <input
// //                   type="text"
// //                   className="form-control"
// //                   id={`description-${item.id}`}
// //                   value={item.description || ''}
// //                   onChange={(e) => handleChange('description', e.target.value)}
// //                   placeholder="Beskrivning av artikeln"
// //                 />
// //               </div>
// //             </div>
// //             <div className="row gx-2 mb-3">
// //               <div className="col">
// //                             <label htmlFor={`quantity-${item.id}`} className="form-label">Antal</label>
// //                             <input
// //                               type="number"
// //                               className="form-control"
// //                               id={`quantity-${item.id}`}
// //                               min="1"
// //                               value={item.quantity || 1}
// //                               onChange={(e) => handleChange('quantity', Math.max(1, parseInt(e.target.value) || 0))}
// //                             />
// //                           </div>
                        
// //                           <div className="col">
// //                             <label htmlFor={`unit-${item.id}`} className="form-label">Enhet</label>
// //                             <select
// //                               className="form-select"
// //                               id={`unit-${item.id}`}
// //                               value={item.unit || 'st'}
// //                               onChange={(e) => handleChange('unit', e.target.value)}
// //                             >
// //                               <option value="st">st</option>
// //                               <option value="tim">tim</option>
// //                               <option value="dag">dag</option>
// //                               <option value="mån">mån</option>
// //                               <option value="frp">frp</option>
// //                               <option value="kg">kg</option>
// //                               <option value="m">m</option>
// //                             </select>
// //                           </div>
                        
// //                           <div className="col">
// //                             <label htmlFor={`type-${item.id}`} className="form-label">Artikeltyp</label>
// //                             <select
// //                               className="form-select"
// //                               id={`type-${item.id}`}
// //                               value={item.type || 'Product'}
// //                               onChange={(e) => handleChange('type', e.target.value)}
// //                             >
// //                               <option value="Product">Produkt</option>
// //                               <option value="Service">Tjänst</option>
// //                               <option value="Text">Textrad</option>
// //                             </select>
// //                           </div>
                        
// //                           <div className="col">
// //                             <label htmlFor={`unitprice-${item.id}`} className="form-label">Pris (kr)</label>
// //                             <input
// //                               type="number"
// //                               className="form-control"
// //                               id={`unitprice-${item.id}`}
// //                               min="0"
// //                               step="0.01"
// //                               value={item.unitprice || 0}
// //                               onChange={(e) => handleChange('unitprice', parseFloat(e.target.value) || 0)}
// //                             />
// //                           </div>
                        
// //                           <div className="col">
// //                             <label htmlFor={`tax_rate-${item.id}`} className="form-label">Moms (%)</label>
// //                             <select
// //                               className="form-select"
// //                               id={`tax_rate-${item.id}`}
// //                               value={item.tax_rate?.replace('%', '') || defaultVatRate}
// //                               onChange={(e) => handleChange('tax_rate', `${e.target.value}%`)}
// //                             >
// //                               <option value="0">0%</option>
// //                               <option value="6">6%</option>
// //                               <option value="12">12%</option>
// //                               <option value="25">25%</option>
// //                             </select>
// //                           </div>
// //                         </div>

// //             <div className="d-flex justify-content-between align-items-center pt-2 border-top">
// //               <span className="text-muted">
// //                 Delsumma: {formatCurrency(subtotal)}
// //               </span>
// //               <span className="fw-medium">
// //                 Moms ({item.tax_rate || '0%'}): {formatCurrency(taxAmount)}
// //               </span>
// //             </div>
// //           </div>
// //         )}
// //       </div>
// //     </div>
// //   );
// // };

// // export default QuoteItemBootstrap;

// // import React, { useState } from 'react';

// // const QuoteItemBootstrap = ({ 
// //   item, 
// //   onUpdate, 
// //   onRemove,
// //   availableItems = [],
// //   index,
// //   isLast
// // }) => {
// //   const [expanded, setExpanded] = useState(false);

// //   const handleChange = (field, value) => {
// //     onUpdate({
// //       ...item,
// //       [field]: field === 'price' || field === 'quantity' || field === 'tax' 
// //         ? parseFloat(value) || 0 
// //         : value
// //     });
// //   };

// //   const handleSelectItem = (e) => {
// //     const selectedItemId = e.target.value;
// //     if (selectedItemId === '') return;
    
// //     const selectedItem = availableItems.find(i => i.id === selectedItemId);
// //     if (selectedItem) {
// //       onUpdate({
// //         ...item,
// //         name: selectedItem.name,
// //         description: selectedItem.description,
// //         price: selectedItem.price,
// //         tax: selectedItem.tax || 0
// //       });
// //     }
// //   };

// //   const subtotal = (item.price * item.quantity) || 0;
// //   const taxAmount = subtotal * (item.tax / 100) || 0;
// //   const total = subtotal + taxAmount;

// //   return (
// //     <div className="card mb-3 border-0 shadow-sm animate__animated animate__fadeIn">
// //       <div className="card-body">
// //         <div className="d-flex justify-content-between align-items-center mb-3">
// //           <h6 className="card-title mb-0">Item #{index + 1}</h6>
// //           <div>
// //             <button
// //               type="button"
// //               className="btn btn-sm btn-link text-primary"
// //               onClick={() => setExpanded(!expanded)}
// //             >
// //               {expanded ? 'Collapse' : 'Expand'}
// //             </button>
// //             <button 
// //               type="button" 
// //               className="btn btn-sm btn-link text-danger ms-2"
// //               onClick={onRemove}
// //               aria-label="Remove item"
// //             >
// //               Remove
// //             </button>
// //           </div>
// //         </div>

// //         <div className="row mb-3">
// //           <div className="col-md-6 mb-3 mb-md-0">
// //             <input
// //               type="text"
// //               className="form-control"
// //               placeholder="Item name"
// //               value={item.name || ''}
// //               onChange={(e) => handleChange('name', e.target.value)}
// //             />
// //           </div>
// //           {availableItems.length > 0 && (
// //             <div className="col-md-6">
// //               <select
// //                 className="form-select"
// //                 onChange={handleSelectItem}
// //                 value=""
// //               >
// //                 <option value="">Select a predefined item...</option>
// //                 {availableItems.map((preItem) => (
// //                   <option key={preItem.id} value={preItem.id}>
// //                     {preItem.name} (${preItem.price.toFixed(2)})
// //                   </option>
// //                 ))}
// //               </select>
// //             </div>
// //           )}
// //         </div>

// //         {expanded && (
// //           <div className="animate__animated animate__fadeIn">
// //             <div className="mb-3">
// //               <textarea
// //                 className="form-control"
// //                 placeholder="Description"
// //                 value={item.description || ''}
// //                 onChange={(e) => handleChange('description', e.target.value)}
// //                 rows="2"
// //               ></textarea>
// //             </div>
// //           </div>
// //         )}

// //         <div className="row">
// //           <div className="col-md-3 col-6 mb-3">
// //             <label className="form-label small text-muted">Price</label>
// //             <div className="input-group">
// //               <span className="input-group-text">$</span>
// //               <input
// //                 type="number"
// //                 className="form-control"
// //                 min="0"
// //                 step="0.01"
// //                 value={item.price || ''}
// //                 onChange={(e) => handleChange('price', e.target.value)}
// //               />
// //             </div>
// //           </div>
// //           <div className="col-md-3 col-6 mb-3">
// //             <label className="form-label small text-muted">Qty</label>
// //             <input
// //               type="number"
// //               className="form-control"
// //               min="1"
// //               value={item.quantity || ''}
// //               onChange={(e) => handleChange('quantity', e.target.value)}
// //             />
// //           </div>
// //           <div className="col-md-3 col-6 mb-3">
// //             <label className="form-label small text-muted">Tax (%)</label>
// //             <input
// //               type="number"
// //               className="form-control"
// //               min="0"
// //               step="0.1"
// //               value={item.tax || ''}
// //               onChange={(e) => handleChange('tax', e.target.value)}
// //             />
// //           </div>
// //           <div className="col-md-3 col-6 mb-3">
// //             <label className="form-label small text-muted">Total</label>
// //             <div className="input-group">
// //               <span className="input-group-text">$</span>
// //               <input
// //                 type="text"
// //                 className="form-control bg-light"
// //                 value={total.toFixed(2)}
// //                 readOnly
// //               />
// //             </div>
// //           </div>
// //         </div>

// //         {expanded && (
// //           <div className="row mt-2 text-muted small animate__animated animate__fadeIn">
// //             <div className="col-md-4">
// //               <div>Subtotal: ${subtotal.toFixed(2)}</div>
// //             </div>
// //             <div className="col-md-4">
// //               <div>Tax ({item.tax}%): ${taxAmount.toFixed(2)}</div>
// //             </div>
// //           </div>
// //         )}
// //       </div>
      
// //       {isLast && (
// //         <div className="card-footer bg-transparent border-top-0 text-center">
// //           <button 
// //             type="button" 
// //             className="btn btn-sm btn-outline-secondary"
// //             onClick={() => onRemove('add')}
// //           >
// //             + Add Another Item
// //           </button>
// //         </div>
// //       )}
// //     </div>
// //   );
// // };

// // export default QuoteItemBootstrap;
